import dayjs from 'dayjs';

export type BookingCalendarData = {
  title: string;
  startDate: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  description?: string;
  location?: string;
};

/**
 * Format dates for ICS (YYYYMMDDTHHMMSS)
 */
const formatICSDate = (date: dayjs.Dayjs): string => {
  return date.format('YYYYMMDDTHHmmss');
};

/**
 * Generate a single event for ICS file
 */
const generateICSEvent = (booking: BookingCalendarData, index: number): string => {
  const { title, startDate, startTime, endDate, endTime, description, location } = booking;

  // Parse start date - handle ISO format and regular date format
  let startDateTime: dayjs.Dayjs;
  if (startTime) {
    // If we have time, parse date and time together
    const dateOnly = dayjs(startDate).format('YYYY-MM-DD');
    startDateTime = dayjs(`${dateOnly} ${startTime}`, 'YYYY-MM-DD HH:mm');
  } else {
    // Just date, set to start of day
    startDateTime = dayjs(startDate).startOf('day');
  }

  // Parse end date/time
  let endDateTime: dayjs.Dayjs;
  if (endDate) {
    if (endTime) {
      const dateOnly = dayjs(endDate).format('YYYY-MM-DD');
      endDateTime = dayjs(`${dateOnly} ${endTime}`, 'YYYY-MM-DD HH:mm');
    } else {
      endDateTime = dayjs(endDate).endOf('day');
    }
  } else {
    // Default to 1 hour after start if no end date
    endDateTime = startDateTime.add(1, 'hour');
  }

  const now = dayjs().format('YYYYMMDDTHHmmss');
  const uid = `${now}-${index}@caretailor.com`;

  return [
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${formatICSDate(startDateTime)}`,
    `DTEND:${formatICSDate(endDateTime)}`,
    `SUMMARY:${title}`,
    description ? `DESCRIPTION:${description.replace(/\n/g, '\\n')}` : '',
    location ? `LOCATION:${location}` : '',
    'STATUS:CONFIRMED',
    'END:VEVENT'
  ]
    .filter(line => line)
    .join('\r\n');
};

/**
 * Generates an ICS file content for a single booking and triggers download
 */
export const exportBookingToCalendar = (booking: BookingCalendarData): void => {
  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//CareTailor//Booking//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    generateICSEvent(booking, 0),
    'END:VCALENDAR'
  ].join('\r\n');

  // Create blob and trigger download
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  const safeDate = dayjs(booking.startDate).isValid() 
    ? dayjs(booking.startDate).format('YYYY-MM-DD')
    : dayjs().format('YYYY-MM-DD');
  link.download = `booking-${safeDate}.ics`;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up
  window.URL.revokeObjectURL(link.href);
};

/**
 * Generates an ICS file for multiple bookings and triggers download
 */
export const exportAllBookingsToCalendar = (bookings: BookingCalendarData[]): void => {
  if (bookings.length === 0) return;

  const events = bookings.map((booking, index) => generateICSEvent(booking, index)).join('\r\n');

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//CareTailor//Bookings//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    events,
    'END:VCALENDAR'
  ].join('\r\n');

  // Create blob and trigger download
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = `all-bookings-${dayjs().format('YYYY-MM-DD')}.ics`;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up
  window.URL.revokeObjectURL(link.href);
};
